import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import UnifiedNavHeader from "../../../../components/navigation/UnifiedNavHeader";
import MobileSportsBar from "../../../../components/homePagePanel/MobileSportsBar";
import { motion } from 'framer-motion';
import { AlertCircle } from 'lucide-react';
import nbaService from '../services/nbaService';
import MobileNBAPredictionSheet from '../components/mobile/MobileNBAPredictionSheet';
import { useQuickPrediction } from '../../../../components/dashboards/hooks/useQuickPrediction';
import QuickPickUpgradeNotice from '../../../../components/QuickPickUpgradeNotice';
import { getBrazilianStatus } from '../../../../utils/detectBrazil';
import { nbaDashPtBR } from '../../../../components/mappings/sports-dash-mappings/nba-dash.pt-br';
import { Header } from '../../../../components/dashboards/components/shared/Header';
import { NBA_HEADER_COPY } from '../constants/headerContent';
import ErrorBoundary from '../../../../components/shared/error/ErrorBoundary';
import MobileNBAGamesView from '../components/mobile/MobileNBAGamesView';
import NBAOddsModal from "../NBAOddsModal";
import RailNavigation from '../../../../components/dashboards/mobile/RailNavigation';
import { MarketSheet } from '../../../../components/dashboards/mobile/FilterSheets';
import useNBARailFilters from '../../../../components/dashboards/mobile/hooks/useNBARailFilters';
import { DatePickerSheet } from '../../../../components/dashboards/components/shared/date-picker';
import { ensureISODate } from '../../../../components/dashboards/utils/dateUtils';

const DEFAULT_GAMES_PAGINATION = {
  currentPage: 1,
  perPage: 6,
  totalPages: 1
};

const NBAMobileDashboard = () => {
  // Internationalizationch
  const isBrazilian = getBrazilianStatus();

  const t = useCallback(
    (text) => (isBrazilian ? nbaDashPtBR[text] || text : text),
    [isBrazilian]
  );

  const [selectedDate, setSelectedDate] = useState(nbaService.getTodayDate());
  const [filterStatus, setFilterStatus] = useState('all');
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gamesPagination, setGamesPagination] = useState(DEFAULT_GAMES_PAGINATION);
  const [selectedGameForOdds, setSelectedGameForOdds] = useState(null);
  const [isPredictionSheetOpen, setIsPredictionSheetOpen] = useState(false);
  const [selectedGameForPrediction, setSelectedGameForPrediction] = useState(null);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [predictionRequestId, setPredictionRequestId] = useState(0);
  const activePredictionRequestRef = useRef(null);
  const [predictionFlowActive, setPredictionFlowActive] = useState(false);

  // Handle rail filter changes
  const handleRailFiltersChange = useCallback((nextFilters) => {
    if (nextFilters.date) {
      setSelectedDate(ensureISODate(nextFilters.date, selectedDate));
    }
    if (nextFilters.status) {
      setFilterStatus(nextFilters.status);
    }
  }, [selectedDate]);

  // Initialize rail filters hook
  const {
    filters: railFilters,
    activeTab: railActiveTab,
    hasActiveFilters: railHasActiveFilters,
    primaryTabs: railPrimaryTabs,
    filterObjects: railFilterObjects,
    handleTabChange: handleRailTabChange,
    handleFilterClick,
    updateFilter: updateRailFilter,
    resetFilters: resetRailFilters,
    showDateSheet,
    setShowDateSheet,
    showStatusSheet,
    setShowStatusSheet
  } = useNBARailFilters({
    initialDate: selectedDate,
    onFiltersChange: handleRailFiltersChange
  });

  // Prediction hook
  const {
    isLoading: isPredicting,
    selectedGames,
    usageState,
    error: predictionError,
    showUpgradeNotice,
    refundNotice,
    toggleGameSelection,
    clearSelections,
    predictSelectedGames,
    setShowUpgradeNotice,
    getGamePrediction,
    isGameSelected,
    selectionCount,
    frozenSelections,
    checkCachedPredictions,
    isGameFrozen
  } = useQuickPrediction();

  // Load games - using multi-season approach like NFL
  const loadGames = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await nbaService.getGamesForSeasons(
        selectedDate, // date
        [2025, 2024, 2023], // seasons to try
        'standard', // league
        null, // team
        'America/New_York' // timezone
      );

      setGames(response?.response || []);

      // Log any errors from specific seasons
      if (response.errors && response.errors.length > 0) {
        console.warn('Some seasons failed to load:', response.errors);
      }
    } catch (err) {
      setError(err.message || t('Failed to load games'));
      console.error('Failed to load NBA games:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, t]);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  // Check for cached predictions when games load
  useEffect(() => {
    const checkCached = async () => {
      if (games.length > 0) {
        const gameIds = games.map(game => game.id).filter(Boolean);

        if (gameIds.length > 0) {
          const cachedPreds = await checkCachedPredictions('nba', gameIds);
          if (Object.keys(cachedPreds).length > 0) {
            console.log(`[NBAMobileDashboard] Loaded ${Object.keys(cachedPreds).length} cached predictions`);
          }
        }
      }
    };
    checkCached();
  }, [games, checkCachedPredictions]);

  const getLiveGames = useCallback(() => {
    return games.filter(game => game?.status?.short === 2);
  }, [games]);

  const getUpcomingGames = useCallback(() => {
    return games.filter(game => game?.status?.short === 1);
  }, [games]);

  const getFinishedGames = useCallback(() => {
    return games.filter(game => game?.status?.short === 3);
  }, [games]);

  const getFilteredGames = useCallback(() => {
    if (filterStatus === 'live') return getLiveGames();
    if (filterStatus === 'upcoming') return getUpcomingGames();
    if (filterStatus === 'finished') return getFinishedGames();
    // Sort by priority: Live > Upcoming > Finished
    return [...getLiveGames(), ...getUpcomingGames(), ...getFinishedGames()];
  }, [games, filterStatus, getLiveGames, getUpcomingGames, getFinishedGames]);

  const filteredGames = useMemo(() => getFilteredGames(), [getFilteredGames]);

  const handleGamesPageChange = useCallback((page) => {
    setGamesPagination((prev) => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.totalPages))
    }));
  }, []);

  useEffect(() => {
    setGamesPagination((prev) => {
      const totalPages = Math.max(1, Math.ceil(filteredGames.length / prev.perPage));
      return { ...prev, totalPages, currentPage: Math.min(prev.currentPage, totalPages) };
    });
  }, [filteredGames]);

  useEffect(() => {
    setGamesPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [filterStatus, selectedDate]);

  const handleViewOdds = useCallback((game) => {
    setSelectedGameForOdds(game);
  }, []);

  const handleCloseOdds = useCallback(() => setSelectedGameForOdds(null), []);

  // Calculate new selections (excluding frozen games with predictions)
  const newSelectionCount = useMemo(() => {
    return selectedGames.filter(game => !isGameFrozen(game.id)).length;
  }, [selectedGames, isGameFrozen]);

  const handleViewPrediction = useCallback(
    (game) => {
      if (!game) return;
      const gamePrediction = getGamePrediction(game.id);
      setSelectedGameForPrediction(game);
      setSelectedPrediction(gamePrediction);
      setIsPredictionSheetOpen(true);
    },
    [getGamePrediction]
  );

  const handleClosePredictionSheet = useCallback(() => {
    setIsPredictionSheetOpen(false);
    setSelectedGameForPrediction(null);
    setSelectedPrediction(null);
  }, []);

  const handlePredictWinners = useCallback(async () => {
    // Track each explicit user request so we only auto-close after a real submission starts/finishes
    setPredictionRequestId((prev) => {
      const next = prev + 1;
      activePredictionRequestRef.current = next;
      return next;
    });
    setPredictionFlowActive(true);
    await predictSelectedGames('nba');
    setPredictionFlowActive(false);
  }, [predictSelectedGames]);

  useEffect(() => {
    if (isPredictionSheetOpen && selectedGameForPrediction) {
      const updatedPrediction = getGamePrediction(selectedGameForPrediction.id);
      setSelectedPrediction(updatedPrediction);
    }
  }, [isPredictionSheetOpen, selectedGameForPrediction, getGamePrediction]);

  useEffect(() => {
    // Mark that a prediction cycle actually started
    if (isPredicting) {
      return;
    }

    const shouldFinalize =
      !isPredicting &&
      !predictionFlowActive &&
      activePredictionRequestRef.current === predictionRequestId &&
      predictionRequestId > 0;

    if (shouldFinalize) {
      clearSelections();
      handleClosePredictionSheet();
      activePredictionRequestRef.current = null;
    }
  }, [
    clearSelections,
    handleClosePredictionSheet,
    isPredicting,
    predictionFlowActive,
    predictionRequestId
  ]);

  return (
    <ErrorBoundary onReset={loadGames}>
      <div className="min-h-screen bg-[#050311] text-white">
        <UnifiedNavHeader title="NBA Dashboard" showBackButton />

        {/* Mobile Sports Navigation Bar */}
        <MobileSportsBar />

        <div className="px-4 py-6 pb-24">
          <Header t={t} {...NBA_HEADER_COPY} />

          <div className="mt-4 mb-4">
            <RailNavigation
              sport="nba"
              primaryTabs={railPrimaryTabs}
              filters={railFilterObjects}
              activeTab={railActiveTab}
              onTabChange={handleRailTabChange}
              onFilterChange={handleFilterClick}
              onReset={resetRailFilters}
              hasActiveFilters={railHasActiveFilters}
              className="border-y border-white/10 bg-[#050311]/95 backdrop-blur"
            />
          </div>

          {error && (
            <div className="flex items-center gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
              <AlertCircle className="text-red-300" size={18} />
              <p>{error}</p>
            </div>
          )}

          {/* Games List */}
          <MobileNBAGamesView
            games={filteredGames}
            loading={loading}
            pagination={gamesPagination}
            onPageChange={handleGamesPageChange}
            t={t}
            isGameSelected={isGameSelected}
            isGameFrozen={isGameFrozen}
            onToggleSelection={toggleGameSelection}
            onViewPrediction={handleViewPrediction}
            getGamePrediction={getGamePrediction}
            onViewOdds={handleViewOdds}
          />

          {/* Mobile Prediction Sheet */}
          <MobileNBAPredictionSheet
            isOpen={isPredictionSheetOpen}
            game={selectedGameForPrediction}
            prediction={selectedPrediction}
            selectionCount={newSelectionCount}
            usageState={usageState}
            isLoading={isPredicting}
            onPredict={handlePredictWinners}
            onClear={clearSelections}
            onClose={handleClosePredictionSheet}
            t={t}
          />

          {/* Upgrade Notice */}
          {showUpgradeNotice && (
            <QuickPickUpgradeNotice
              onClose={() => setShowUpgradeNotice(false)}
              isMobile={true}
              sport="NBA"
            />
          )}

          {selectedGameForOdds && (
            <NBAOddsModal
              game={selectedGameForOdds}
              isOpen={!!selectedGameForOdds}
              onClose={handleCloseOdds}
            />
          )}
        </div>

        {/* Filter Sheets */}
        <DatePickerSheet
          isOpen={showDateSheet}
          onClose={() => setShowDateSheet(false)}
          selectedDate={ensureISODate(railFilters.date || selectedDate)}
          onDateSelect={(isoDate) => {
            const formatted = ensureISODate(isoDate, selectedDate);
            updateRailFilter("date", formatted);
            setSelectedDate(formatted);
            setShowDateSheet(false);
          }}
        />

        <MarketSheet
          isOpen={showStatusSheet}
          onClose={() => setShowStatusSheet(false)}
          currentMarket={railFilters.status || 'all'}
          onMarketChange={(status) => {
            updateRailFilter('status', status);
            setShowStatusSheet(false);
          }}
          sport="nba"
        />
      </div>
    </ErrorBoundary>
  );
};

export default NBAMobileDashboard;
